import { type ComponentType, type FC, type ReactNode, useState } from "react"
import styled from "styled-components"

import Button from "~/components/Button"
import { componentBackgroundColor, componentBorderColor, lightTextColor } from "~/utilities/styles"

type Props = {
	illustration: ComponentType<{ className?: string }>
	title: string
	text: ReactNode
	callToAction?: {
		label: string
		onClick(): Promise<unknown> | unknown
	}
	children?: ReactNode
}

const FeatureIntroduction: FC<Props> = ({ illustration, title, text, callToAction, children }) => {
	const [isSubmitting, setSubmitting] = useState(false)

	return (
		<Container>
			<Illustration as={illustration} />
			<Title>{title}</Title>
			<Text>{text}</Text>
			{callToAction && (
				<CallToAction
					variant="primary"
					onClick={async () => {
						setSubmitting(true)
						await callToAction.onClick()
						setSubmitting(false)
					}}
					isLoading={isSubmitting}
				>
					{callToAction.label}
				</CallToAction>
			)}
			{children && <Children>{children}</Children>}
		</Container>
	)
}

const Container = styled.div`
	background-color: ${componentBackgroundColor};
	border: 1px solid ${componentBorderColor};
	border-radius: 24px;
	padding: clamp(24px, 4vw, 40px);
	display: flex;
	flex-direction: column;
	align-items: center;
`

const Illustration = styled.div`
	width: 200px;
	color: ${props => props.theme.primaryColor};
	margin-bottom: 8px;
`

const Title = styled.h2`
	font-weight: 600;
	font-size: 24px;
	margin-bottom: 12px;
`

const Text = styled.div`
	color: ${lightTextColor};
	text-align: center;
	max-width: 600px;

	> *:not(:last-child) {
		margin-bottom: 12px;
	}

	&:not(:last-child) {
		margin-bottom: 16px;
	}
`

const CallToAction = styled(Button)`
	&:not(:last-child) {
		margin-bottom: 16px;
	}
`

const Children = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`

export default FeatureIntroduction
